import { useEffect, useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import VideoBackground from './components/VideoBackground';
import FloatingOrbs from './components/FloatingOrbs';
import HeroSection from './components/HeroSection';
import CompanyOverviewSection from './components/CompanyOverviewSection';
import ExpertiseSection from './components/ExpertiseSection';
import IndustrySection from './components/IndustrySection';
import ProcessSection from './components/ProcessSection';
import InterimNotice from './components/InterimNotice';
import ContactSection from './components/ContactSection';
import {
  HeroSection as HeroSectionType,
  CompanyOverview,
  ExpertiseCard,
  IndustryApplication,
  ProcessStep } from
'./types';

const LandingPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const heroData: HeroSectionType = {
    headline: "Dhavaja Research & Innovation Labs is a next-gen R&D company focused on electronics and emerging technology.",
    subheadline: "We build, prototype, and deliver cutting-edge products for industry, defense, and tomorrow's smart devices. From sensor systems to industrial IoT, we turn ideas into reality.",
    primaryCTA: "Start Your Project",
    secondaryCTA: "View Capabilities"
  };

  const companyOverviewData: CompanyOverview = {
    title: "Dhavaja Research & Innovation Labs",
    description: "Premier electronics R&D lab delivering field-ready prototypes for mission-critical applications across defense, aerospace, and industrial sectors.",
    highlights: [
    {
      id: "location",
      icon: "MapPin",
      title: "Noida Tech Hub",
      description: "Strategically located in India's premier technology corridor with access to world-class engineering talent and manufacturing capabilities."
    },
    {
      id: "validation",
      icon: "Linkedin",
      title: "Industry Validated",
      description: "Connect with us on LinkedIn to explore our professional network and stay updated on cutting-edge electronics R&D developments."
    },
    {
      id: "portfolio",
      icon: "Shield",
      title: "NDA Protected",
      description: "Extensive portfolio of mission-critical projects protected under strict confidentiality agreements with defense and aerospace clients."
    }]

  };

  const expertiseCards: ExpertiseCard[] = [
  {
    id: "custom-electronics",
    icon: "Cpu",
    title: "Custom Electronics",
    description: "End-to-end development of specialized electronic systems tailored to your exact specifications and operational requirements.",
    capabilities: [
    "PCB design and prototyping with multi-layer complexity",
    "Analog and digital circuit design optimization",
    "Power management and thermal analysis",
    "EMI/EMC compliance testing and certification",
    "Component selection and supply chain management"],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1babc7a58-1764734611268.png",
    alt: "Close-up view of green circuit board with electronic components, resistors, and integrated circuits showing intricate PCB design"
  },
  {
    id: "embedded-iot",
    icon: "Wifi",
    title: "Embedded & IoT",
    description: "Intelligent embedded systems and IoT solutions that connect, monitor, and control critical operations in real-time.",
    capabilities: [
    "ARM Cortex and RISC-V microcontroller programming",
    "Real-time operating systems (RTOS) implementation",
    "Wireless communication protocols (LoRa, Zigbee, BLE)",
    "Edge computing and data processing",
    "Secure firmware development and OTA updates"],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1470b2657-1764669651426.png",
    alt: "Futuristic IoT network visualization with glowing blue circuit connections and data nodes on dark background representing embedded systems"
  },
  {
    id: "uav-defense",
    icon: "Plane",
    title: "UAV & Defense",
    description: "Specialized subsystems for unmanned aerial vehicles and defense applications requiring extreme reliability and performance.",
    capabilities: [
    "Flight control systems and autopilot development",
    "Telemetry and ground control station integration",
    "Payload management and sensor fusion",
    "Ruggedized electronics for harsh environments",
    "MIL-STD compliance and qualification testing"],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_100160cb8-1764707696948.png",
    alt: "Military-grade UAV drone in flight against clear blue sky showing advanced electronics and sensor systems"
  }];


  const industries: IndustryApplication[] = [
  {
    id: "defense",
    name: "Defense",
    useCases: [
    "Tactical communication systems for secure field operations",
    "Ruggedized electronics for armored vehicles and naval vessels",
    "Surveillance and reconnaissance sensor integration",
    "Electronic warfare countermeasure systems"]

  },
  {
    id: "aerospace",
    name: "Aerospace",
    useCases: [
    "Avionics systems for commercial and military aircraft",
    "Satellite communication and telemetry subsystems",
    "Flight data recorders and monitoring systems",
    "Ground support equipment and test fixtures"]

  },
  {
    id: "uav",
    name: "UAV",
    useCases: [
    "Autonomous flight control and navigation systems",
    "Long-range telemetry and video transmission",
    "Payload delivery and precision drop mechanisms",
    "Swarm coordination and multi-UAV communication"]

  },
  {
    id: "robotics",
    name: "Robotics",
    useCases: [
    "Industrial automations",
    "Autonomous hobby robots",
    ,
    ]

  },
  {
    id: "industrial-iot",
    name: "Industrial IoT",
    useCases: [
    "Predictive maintenance sensor networks",
    "Smart factory automation and monitoring",
    "Energy management and optimization systems",
    "Supply chain tracking and asset management"]

  }];


  const processSteps: ProcessStep[] = [
  {
    id: "concept",
    number: 1,
    title: "Concept & Requirements",
    description: "Deep-dive technical consultation to understand your operational needs, constraints, and success criteria.",
    details: [
    "Detailed requirements gathering and feasibility analysis",
    "Technology selection and architecture design",
    "Risk assessment and mitigation planning",
    "Project timeline and milestone definition"],

    icon: "Lightbulb"
  },
  {
    id: "design",
    number: 2,
    title: "Design & Development",
    description: "Iterative design process with regular reviews ensuring alignment with your specifications and industry standards.",
    details: [
    "Schematic design and simulation validation",
    "PCB layout with DFM and DFT considerations",
    "Firmware development and unit testing",
    "Design review and approval checkpoints"],

    icon: "Pencil"
  },
  {
    id: "prototype",
    number: 3,
    title: "Prototype & Testing",
    description: "Rigorous testing protocols to validate performance, reliability, and compliance before field deployment.",
    details: [
    "Prototype fabrication and assembly",
    "Functional testing and performance validation",
    "Environmental testing (temperature, humidity, vibration)",
    "EMI/EMC compliance testing and certification"],

    icon: "TestTube"
  },
  {
    id: "delivery",
    number: 4,
    title: "Field-Ready Delivery",
    description: "Complete documentation, training, and support to ensure successful deployment and operation.",
    details: [
    "Production-ready design files and BOM",
    "Comprehensive technical documentation",
    "Manufacturing support and quality assurance",
    "Post-deployment support and maintenance"],

    icon: "CheckCircle2"
  }];


  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-hidden">
      <VideoBackground />
      <FloatingOrbs />

      <div className="relative z-10">
        <Header />

        <main>
          <HeroSection
            data={heroData}
            onPrimaryCTA={() => scrollToSection('contact')}
            onSecondaryCTA={() => scrollToSection('capabilities')} />


          <CompanyOverviewSection data={companyOverviewData} />

          <ExpertiseSection cards={expertiseCards} />

          <IndustrySection industries={industries} />

          <ProcessSection steps={processSteps} />

          <InterimNotice />

          <ContactSection />
        </main>

        <Footer />

        {showScrollTop &&
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full glass-effect flex items-center justify-center text-primary hover:bg-primary/10 transition-all duration-250 shadow-glass animate-fade-in"
          aria-label="Scroll to top">

            <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">

              <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18" />

            </svg>
          </button>
        }
      </div>
    </div>);

};

export default LandingPage;